/**
 * Utility functions for encoding and decoding search text in URLs
 * Provides a consistent way to handle URL parameters across the application
 */

/**
 * Validates if a string is valid for encoding
 * @param {string} text - The text to validate
 * @returns {boolean} Whether the text is valid
 */
const isValidText = (text) => {
  return typeof text === 'string' && text.length > 0;
};

/**
 * Encodes text to a URL-safe, compressed format
 * @param {string} text - The text to encode
 * @returns {string} The encoded text
 */
export const encodeSearchText = (text) => {
  if (!isValidText(text)) {
    console.warn('Invalid text provided for encoding:', text);
    return '';
  }

  try {
    // First encode to handle all characters
    const encoded = encodeURIComponent(text);
    // Convert to base64
    const base64 = btoa(encoded);
    // Make URL safe and shorter by replacing common patterns
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  } catch (error) {
    console.error('Error encoding search text:', error);
    return '';
  }
};

/**
 * Decodes text from the URL-safe, compressed format
 * @param {string} encoded - The encoded text to decode
 * @returns {string} The decoded text
 */
export const decodeSearchText = (encoded) => {
  if (!isValidText(encoded)) {
    console.warn('Invalid encoded text provided for decoding:', encoded);
    return '';
  }

  try {
    // Restore base64 padding
    let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    // Decode base64
    const decoded = atob(base64);
    // Decode URI component
    return decodeURIComponent(decoded);
  } catch (error) {
    console.error('Error decoding search text:', error);
    return '';
  }
}; 